import { Link, useParams } from "react-router-dom";
import { Fragment, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileDown, Truck, CheckCircle2, FileText, Pencil, Save, X, Tag, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, StatusBadge } from "@/components/Common";
import { EmailDocumentDialog } from "@/components/EmailDocumentDialog";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { ProjectDetailsCard } from "@/components/ProjectDetailsCard";
import { ActivityLog } from "@/components/ActivityLog";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function DeliveryOrderDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["delivery-order", id],
    queryFn: () => api.getDeliveryOrder(id),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState({
    delivery_date: "", delivery_address: "", carrier: "",
    quotation_number: "", rfq_number: "", rfp_number: "",
    tender_name: "", tender_number: "", contract_number: "", po_number: "",
    notes: "",
  });

  useEffect(() => {
    if (data) {
      setForm({
        delivery_date: (data.delivery_date || "").slice(0, 10),
        delivery_address: data.delivery_address || "",
        carrier: data.carrier || "",
        quotation_number: data.quotation_number || "",
        rfq_number: data.rfq_number || "",
        rfp_number: data.rfp_number || "",
        tender_name: data.tender_name || "",
        tender_number: data.tender_number || "",
        contract_number: data.contract_number || "",
        po_number: data.po_number || "",
        notes: data.notes || "",
      });
    }
  }, [data]);

  const [editingLine, setEditingLine] = useState(null); // line index being edited
  const [serialBuf, setSerialBuf] = useState([]);

  const statusMut = useMutation({
    mutationFn: (s) => api.updateDeliveryStatus(id, s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-order", id] });
      qc.invalidateQueries({ queryKey: ["delivery-orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Status updated");
    },
  });

  const updateMut = useMutation({
    mutationFn: (body) => api.updateDeliveryOrder(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-order", id] });
      qc.invalidateQueries({ queryKey: ["delivery-orders"] });
      setEditOpen(false);
      toast.success(t("do_info_saved"));
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const serialsMut = useMutation({
    mutationFn: (payload) => api.updateDeliveryLineSerials(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["delivery-order", id] });
      qc.invalidateQueries({ queryKey: ["client-assets"] });
      setEditingLine(null);
      toast.success(t("serials_saved"));
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: (() => {
      const d = new Date(); d.setDate(d.getDate() + 30);
      return d.toISOString().slice(0, 10);
    })(),
    terms: "Net 30 days",
  });

  const invoiceMut = useMutation({
    mutationFn: (body) => api.createInvoiceFromDO(id, body || {}),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["delivery-order", id] });
      toast.success(`Invoice created: ${inv.invoice_number}`);
      setCreateInvoiceOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  function applyPaste(text) {
    const lines = text.split(/[\r\n,;\t]+/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setSerialBuf((prev) => {
      const next = [...prev];
      for (let i = 0; i < next.length && i < lines.length; i++) {
        next[i] = lines[i];
      }
      return next;
    });
    toast.success(`${Math.min(lines.length, serialBuf.length)} serial(s) applied`);
  }

  function openSerialEditor(idx, line) {
    const qty = Number(line.qty || 0);
    const existing = Array.isArray(line.serials) ? line.serials : [];
    const buf = Array.from({ length: qty }, (_, i) => existing[i] || "");
    setSerialBuf(buf);
    setEditingLine(idx);
  }
  function saveSerials() {
    serialsMut.mutate([{ line_index: editingLine, serials: serialBuf }]);
  }

  if (isLoading || !data) return <p className="text-muted-foreground">{t("loading")}</p>;

  const c = data.client_snapshot || {};
  const isFinalised = data.status === "delivered" || data.status === "cancelled";
  const totalQty = (data.items || []).reduce((s, l) => s + Number(l.qty || 0), 0);
  const deliveredQty = (data.items || []).reduce((s, l) => s + Number(l.delivered_qty || 0), 0);

  return (
    <div data-testid="delivery-order-detail-page">
      <Link to="/delivery-orders" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="size-3.5 mr-1" /> {t("delivery_orders")}
      </Link>

      <PageHeader
        overline={t("do_number")}
        title={data.do_number}
        subtitle={
          <span className="inline-flex items-center gap-3 text-sm">
            <span>{t("delivery_date")}: <span className="mono">{fmtDate(data.delivery_date)}</span></span>
            <StatusBadge value={data.status} />
          </span>
        }
        actions={
          <>
            <Button data-testid="edit-do-info-btn" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4 mr-1.5" /> {t("edit_do_info")}
            </Button>
            {data.invoice_id ? (
              <Link to={`/invoices/${data.invoice_id}`}>
                <Button data-testid="goto-invoice-from-do-btn" variant="outline">{t("invoice")}</Button>
              </Link>
            ) : (
              <Button data-testid="create-invoice-from-do-btn" variant="outline"
                onClick={() => setCreateInvoiceOpen(true)} disabled={invoiceMut.isPending}>
                {t("create_invoice")}
              </Button>
            )}
            <Button data-testid="download-pdf-btn" variant="outline" onClick={() => setPreviewOpen(true)}>
              <FileDown className="size-4 mr-1.5" /> {t("download_pdf")}
            </Button>
            <Button data-testid="email-do-btn" variant="outline" onClick={() => setEmailOpen(true)}>
              <Mail className="size-4 mr-1.5" /> Email
            </Button>
            {data.status === "pending" && (
              <Button
                data-testid="mark-in-transit-btn"
                variant="outline"
                onClick={() => statusMut.mutate("in_transit")}
              >
                <Truck className="size-4 mr-1.5" /> {t("mark_in_transit")}
              </Button>
            )}
            {data.status !== "delivered" && data.status !== "cancelled" && (
              <Button
                data-testid="mark-delivered-btn"
                onClick={() => statusMut.mutate("delivered")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 className="size-4 mr-1.5" /> {t("mark_delivered")}
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("client")}</p>
          <p className="font-display font-semibold text-lg">{c.name}</p>
          {c.contact_person && <p className="text-sm text-muted-foreground">{c.contact_person}</p>}
          <div className="mt-3 space-y-1 text-xs mono text-muted-foreground">
            {c.email && <p>{c.email}</p>}
            {c.phone && <p>{c.phone}</p>}
          </div>
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("delivery_address")}</p>
          <p className="text-sm">{data.delivery_address || c.address || "—"}</p>
          {data.carrier && (
            <div className="mt-3">
              <p className="overline mb-1">{t("carrier")}</p>
              <p className="text-sm mono">{data.carrier}</p>
            </div>
          )}
        </div>
        <div className="border border-border rounded-md bg-white p-5" data-testid="tender-po-card">
          <p className="overline mb-2">Tender / PO reference</p>
          <div className="space-y-2 text-sm">
            {data.quotation_number && (
              <div>
                <p className="overline mb-0.5">Quotation No.</p>
                <p data-testid="do-quotation-number" className="mono">{data.quotation_number}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="overline mb-0.5">RFQ No.</p>
                <p data-testid="do-rfq-number" className={`mono ${data.rfq_number ? "" : "text-muted-foreground"}`}>
                  {data.rfq_number || "—"}
                </p>
              </div>
              <div>
                <p className="overline mb-0.5">RFP No.</p>
                <p data-testid="do-rfp-number" className={`mono ${data.rfp_number ? "" : "text-muted-foreground"}`}>
                  {data.rfp_number || "—"}
                </p>
              </div>
            </div>
            <div>
              <p className="overline mb-0.5">{t("tender_name")}</p>
              <p data-testid="do-tender-name" className={data.tender_name ? "" : "text-muted-foreground"}>
                {data.tender_name || "—"}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="overline mb-0.5">{t("tender_number")}</p>
                <p data-testid="do-tender-number" className={`mono ${data.tender_number ? "" : "text-muted-foreground"}`}>
                  {data.tender_number || "—"}
                </p>
              </div>
              <div>
                <p className="overline mb-0.5">Contract No.</p>
                <p data-testid="do-contract-number" className={`mono ${data.contract_number ? "" : "text-muted-foreground"}`}>
                  {data.contract_number || "—"}
                </p>
              </div>
              <div>
                <p className="overline mb-0.5">{t("po_number")}</p>
                <p data-testid="do-po-number" className={`mono ${data.po_number ? "" : "text-muted-foreground"}`}>
                  {data.po_number || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("order_ref")}</p>
          {data.order_id ? (
            <Link to={`/orders/${data.order_id}`} className="text-primary hover:underline mono">
              <FileText className="inline size-3.5 mr-1" />{data.order_number}
            </Link>
          ) : (
            <p className="text-muted-foreground">—</p>
          )}
        </div>
        <div className="border border-border rounded-md bg-white p-5 grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="overline mb-1">Created</p>
            <p className="mono text-muted-foreground">{fmtDateTime(data.created_at)}</p>
          </div>
          {data.delivered_at && (
            <div>
              <p className="overline mb-1">Delivered at</p>
              <p className="mono text-emerald-700">{fmtDateTime(data.delivered_at)}</p>
            </div>
          )}
        </div>
      </div>

      <ProjectDetailsCard
        docId={data.id}
        initial={data.project_details}
        patchFn={api.updateDeliveryOrder}
        invalidateKeys={[["delivery-order", id], ["delivery-orders"]]}
        testIdPrefix="do-project"
      />

      <div className="border border-border rounded-md bg-white overflow-x-auto mb-5" data-testid="do-items-table">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="overline">#</TableHead>
              <TableHead className="overline">{t("item_name")}</TableHead>
              <TableHead className="overline">{t("brand")} / {t("model")}</TableHead>
              <TableHead className="overline">{t("specification")}</TableHead>
              <TableHead className="overline text-right">{t("qty")}</TableHead>
              <TableHead className="overline text-right">{t("unit")}</TableHead>
              <TableHead className="overline text-right">{t("serials")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.items || []).map((l, i) => {
              const filled = (l.serials || []).filter((s) => String(s || "").trim()).length;
              const qty = Number(l.qty || 0);
              return (
                <Fragment key={i}>
                  <TableRow data-testid={`do-line-${i}`}>
                    <TableCell className="mono text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[l.brand, l.model].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.specification || "—"}</TableCell>
                    <TableCell className="text-right mono font-medium">{l.qty}</TableCell>
                    <TableCell className="text-right">
                      {isFinalised ? (
                        <span className="mono font-medium text-emerald-700">{l.delivered_qty ?? l.qty}</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            data-testid={`do-delivered-${i}`}
                            type="number" min="0" step="1"
                            max={Number(l.qty || 0)}
                            value={Number(l.delivered_qty ?? 0)}
                            className="w-16 h-7 text-right mono text-xs"
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(Number(l.qty || 0), Number(e.target.value || 0)));
                              const items = [...(data.items || [])];
                              items[i] = { ...items[i], delivered_qty: v };
                              updateMut.mutate({ items });
                            }}
                          />
                          <Button
                            data-testid={`do-ship-all-${i}`}
                            variant="ghost" size="sm"
                            className="text-[10px] underline text-primary px-1"
                            title="Ship all remaining"
                            onClick={() => {
                              const items = [...(data.items || [])];
                              items[i] = { ...items[i], delivered_qty: Number(items[i].qty || 0) };
                              updateMut.mutate({ items });
                            }}
                          >All</Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{l.unit || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`edit-serials-${i}`}
                        variant="ghost"
                        size="sm"
                        className={filled === qty && qty > 0 ? "text-emerald-700" : "text-primary"}
                        onClick={() => openSerialEditor(i, l)}
                        title={t("edit_serials")}
                      >
                        <Tag className="size-3.5 mr-1" />
                        <span className="mono text-xs">{filled}/{qty}</span>
                      </Button>
                    </TableCell>
                  </TableRow>

                  {editingLine === i && (
                    <TableRow data-testid={`serials-editor-${i}`} className="bg-[#FAFAFA]">
                      <TableCell colSpan={8} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="overline">{t("serials")} — {l.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t("serials_hint")}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              data-testid={`paste-serials-${i}`}
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const text = window.prompt(t("paste_serials_hint"));
                                if (text) applyPaste(text);
                              }}
                            >
                              {t("paste_serials")}
                            </Button>
                            <Button
                              data-testid={`cancel-serials-${i}`}
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingLine(null)}
                            >
                              <X className="size-3.5 mr-1" /> {t("cancel")}
                            </Button>
                            <Button
                              data-testid={`save-serials-${i}`}
                              size="sm"
                              className="bg-primary hover:bg-primary/90"
                              disabled={serialsMut.isPending}
                              onClick={saveSerials}
                            >
                              <Save className="size-3.5 mr-1" /> {t("save_serials")}
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {serialBuf.map((sn, u) => (
                            <div key={u} className="flex items-center gap-2">
                              <span className="overline w-16 shrink-0">{t("unit_n")} #{u + 1}</span>
                              <Input
                                data-testid={`serial-input-${i}-${u}`}
                                className="mono"
                                placeholder="SN-..."
                                value={sn}
                                onChange={(e) => {
                                  const next = [...serialBuf];
                                  next[u] = e.target.value;
                                  setSerialBuf(next);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const nextInput = document.querySelector(
                                      `[data-testid="serial-input-${i}-${u + 1}"]`
                                    );
                                    if (nextInput) nextInput.focus();
                                    else saveSerials();
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end" data-testid="do-summary">
        <div className="w-full max-w-sm border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("summary")}</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("lines")}</span>
              <span className="mono font-medium" data-testid="do-lines-count">{(data.items || []).length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("units")}</span>
              <span className="mono font-medium" data-testid="do-units-count">
                {totalQty}
              </span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 mt-2">
              <span className="text-muted-foreground">Delivered</span>
              <span data-testid="do-delivered-count"
                    className={`mono font-bold ${deliveredQty >= totalQty && totalQty > 0 ? "text-emerald-700" : deliveredQty > 0 ? "text-amber-700" : "text-rose-700"}`}>
                {deliveredQty} / {totalQty}
              </span>
            </div>
            {totalQty > 0 && (
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="do-progress-bar">
                <div
                  className={`h-full transition-all ${deliveredQty >= totalQty ? "bg-emerald-500" : deliveredQty > 0 ? "bg-amber-500" : "bg-slate-300"}`}
                  style={{ width: `${Math.min(100, (deliveredQty / totalQty) * 100)}%` }}
                />
              </div>
            )}
            <p className="text-[11px] text-muted-foreground border-t border-border pt-2 mt-2">
              {t("pricing_disclaimer")}
            </p>
          </div>
        </div>
      </div>

      {data.notes && (
        <div className="border border-border rounded-md bg-white p-5 mt-5">
          <p className="overline mb-2">{t("notes")}</p>
          <p className="text-sm whitespace-pre-wrap">{data.notes}</p>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-do-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{t("edit_do_info")}</DialogTitle>
            <DialogDescription className="text-xs">
              {data.do_number}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">{t("delivery_date")}</Label>
              <Input
                data-testid="edit-do-delivery-date"
                type="date"
                value={form.delivery_date}
                onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("carrier")}</Label>
              <Input
                data-testid="edit-do-carrier"
                value={form.carrier}
                onChange={(e) => setForm({ ...form, carrier: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("delivery_address")}</Label>
              <Textarea
                data-testid="edit-do-delivery-address"
                rows={2}
                value={form.delivery_address}
                onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2 border-t border-border pt-3">
              <p className="overline">Tender / PO reference</p>
            </div>
            <div>
              <Label className="overline">Quotation No.</Label>
              <Input
                data-testid="edit-do-quotation-number"
                className="mono"
                value={form.quotation_number}
                onChange={(e) => setForm({ ...form, quotation_number: e.target.value })}
                placeholder="QT-…"
              />
            </div>
            <div>
              <Label className="overline">RFQ No.</Label>
              <Input
                data-testid="edit-do-rfq-number"
                className="mono"
                value={form.rfq_number}
                onChange={(e) => setForm({ ...form, rfq_number: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">RFP No.</Label>
              <Input
                data-testid="edit-do-rfp-number"
                className="mono"
                value={form.rfp_number}
                onChange={(e) => setForm({ ...form, rfp_number: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Contract No.</Label>
              <Input
                data-testid="edit-do-contract-number"
                className="mono"
                value={form.contract_number}
                onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("tender_name")}</Label>
              <Input
                data-testid="edit-do-tender-name"
                value={form.tender_name}
                onChange={(e) => setForm({ ...form, tender_name: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("tender_number")}</Label>
              <Input
                data-testid="edit-do-tender-number"
                className="mono"
                value={form.tender_number}
                onChange={(e) => setForm({ ...form, tender_number: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("po_number")}</Label>
              <Input
                data-testid="edit-do-po-number"
                className="mono"
                value={form.po_number}
                onChange={(e) => setForm({ ...form, po_number: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("notes")}</Label>
              <Textarea
                data-testid="edit-do-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-do-cancel">
              {t("cancel")}
            </Button>
            <Button
              data-testid="edit-do-save"
              className="bg-primary hover:bg-primary/90"
              disabled={updateMut.isPending}
              onClick={() => updateMut.mutate(form)}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailDocumentDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        kind="delivery_order"
        defaultTo={data.client_snapshot?.email || ""}
        defaultClientName={data.client_snapshot?.name || ""}
        sendFn={(payload) => api.emailDeliveryOrder(data.id, payload)}
        testIdPrefix="email-do"
      />

      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={`Delivery Order ${data?.do_number || ""}`}
        filename={`delivery-order-${data?.do_number || data?.id}.pdf`}
        fetchBlob={() => api.fetchDeliveryOrderPdfBlob(data.id)}
      />

      <Dialog open={createInvoiceOpen} onOpenChange={setCreateInvoiceOpen}>
        <DialogContent data-testid="create-invoice-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Create invoice from delivery</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Set the invoice date, due date, and payment terms. Lines &amp; totals
              are copied from this delivery order.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Invoice date</Label>
              <Input
                data-testid="new-inv-issue-date"
                type="date"
                value={invoiceForm.issue_date}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, issue_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Due date</Label>
              <Input
                data-testid="new-inv-due-date"
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="overline">Payment terms</Label>
              <Textarea
                data-testid="new-inv-terms"
                rows={2}
                value={invoiceForm.terms}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, terms: e.target.value })}
                placeholder="Net 30 days · Bank transfer to …"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateInvoiceOpen(false)}
                    data-testid="new-inv-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => invoiceMut.mutate({
                issue_date: invoiceForm.issue_date,
                due_date: invoiceForm.due_date,
                terms: invoiceForm.terms,
              })}
              disabled={invoiceMut.isPending}
              className="bg-primary hover:bg-primary/90"
              data-testid="new-inv-submit"
            >
              {invoiceMut.isPending ? "Creating…" : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8">
        <ActivityLog resourceType="delivery_order" resourceId={data.id} />
      </div>
    </div>
  );
}
